/**
 * Semantic phrasing for audit actions: what a READER should be told happened.
 *
 * The rail used to print the raw action, upper-cased — "CANVAS PROJECTED",
 * "PROJECTION PING". Those are implementation events; a history surface should
 * say what someone did. This table is the seam that turns one into the other.
 *
 * WHY IT LIVES HERE. `packages/ui` is data-blind (ADR-0009) so a phrase table
 * cannot go in `AuditFeed`, and a `*-feature` package may not import a sibling,
 * so a mapper in `solve-feature` would become three copies — which already
 * happened once to the stream merge, as `entity-audit-merge.ts` records. This
 * package already owns the shared view model AND an action table
 * (`INTERNAL_RECEIPT_ACTIONS`), so the precedent is in the function this
 * extends.
 *
 * KEYED ON `entityType|action`, NEVER ACTION ALONE. `created`/`updated` are
 * written on both `solve` and `solve_membership`; `create`/`update` collide
 * between the KPI and Integrations vocabularies. An action-only key mislabels
 * rows — "created this SOLVE" for a membership row.
 *
 * PHRASES COMPLETE A SENTENCE the component starts with the actor: the rail
 * renders the actor name on one line and this on the next, so "Priya Shah" +
 * "updated the target" reads as one statement without duplicating the name.
 *
 * ADD, NEVER RENAME. `audit_log` has UPDATE/DELETE revoked (ADR-0005), so
 * history holds action strings whose vocabulary has since moved on — Hoshin
 * keeps `member_permission_changed` for exactly that reason. A phrase may be
 * reworded freely (it is display only); a KEY may not be dropped.
 */

import {
  VSM_ARTIFACT_LABELS,
  VSM_FLOW_LABELS,
  VSM_SHAPE_CATALOGUE,
  XMATRIX_ITEM_LABELS,
  HOSHIN_STEP_LABELS,
} from "@playbookai/types";
import { WIDGET_LABELS } from "@playbookai/widget-schemas";

/** What to tell the reader. `predicate` completes "<Actor> …". */
export interface AuditPhrase {
  readonly predicate: string;
  /**
   * PB-5161 — the same sentence with the OBJECT named, when the row carries an
   * allowlisted `objectKind` the shared catalogues can label: "added a Problem
   * Statement" rather than "added a widget".
   *
   * ADDITIVE and optional by design. Every entry without it keeps rendering
   * `predicate` exactly as before, so widening the vocabulary is never a
   * rewording of history that already reads correctly.
   *
   * It takes the resolved LABEL, not the kind, so the phrase table never has
   * to know which catalogue a kind came from.
   */
  readonly withObject?: (label: string) => string;
  /**
   * PB-5174 — the same sentence for the actor's Nth such object: "added sticky
   * note 3", "added KPI tile 2".
   *
   * It restates the whole clause rather than receiving a suffix, because the
   * unnumbered wording carries an indefinite article the counted wording must
   * drop: "added a sticky note" cannot become "added a sticky note 3".
   *
   * `label` is the resolved object label when the row carries one, so an entry
   * can name the object AND count it in one form. It is `undefined` for the
   * families that have no catalogue kind (sticky notes, VSM swimlanes), which
   * is why the parameter is optional rather than two separate hooks.
   *
   * ADDITIVE and optional, like `withObject`: an entry without it keeps
   * rendering the unnumbered wording however many times it repeats.
   */
  readonly withCount?: (n: number, label?: string) => string;
}

/**
 * Split the runtime `_locked` suffix off an action.
 *
 * `lockedActionSuffix()` in the widgets canvas gate appends `_locked` when a
 * write lands on a locked canvas, so a large part of the vocabulary exists in
 * two forms in `audit_log`. A lookup that does not strip it silently falls back
 * to the raw-action rendering for every write made while a canvas was locked —
 * which is not a rare state.
 */
/**
 * Actions whose OWN name ends in `_locked`: the lock VERBS themselves, not a
 * write that `lockedActionSuffix()` suffixed. Stripping the suffix off one of
 * these produces a key nothing maps — `solve|phase_locked` would become
 * `solve|phase` — which is exactly the raw-action fallback ("PHASE LOCKED")
 * this normalisation exists to prevent. Both members are written today:
 * `canvas_locked` by solves/vsms/hoshin canvas-lock, `phase_locked` by
 * `solves.service.ts` against entityType `solve`. ADD a member here whenever a
 * new lock verb ships.
 */
const LITERAL_LOCKED_ACTIONS: ReadonlySet<string> = new Set([
  "canvas_locked",
  "phase_locked",
]);

export function normalizeAuditAction(action: string): {
  readonly action: string;
  readonly locked: boolean;
} {
  return action.endsWith("_locked") && !LITERAL_LOCKED_ACTIONS.has(action)
    ? { action: action.slice(0, -"_locked".length), locked: true }
    : { action, locked: false };
}

const phrase = (predicate: string): AuditPhrase => ({ predicate });

/**
 * A phrase that names the object when one is known, and falls back to
 * `predicate` when it is not (PB-5161).
 *
 * `withObject` receives the resolved catalogue label — see
 * {@link auditObjectLabel} — never raw user text.
 *
 * NO INDEFINITE ARTICLE in the object form, deliberately. The catalogue holds
 * both vowel-initial and plural labels, so a hardcoded "a" produces "added a
 * Impact / Effort Matrix" and "added a Tags"; picking a/an from the first
 * letter still fails the plurals. Dropping the article is the only form that
 * is correct for every label without per-label grammar data, and the label is
 * a proper name, so "added Problem Statement" reads naturally.
 */
const objectPhrase = (
  predicate: string,
  withObject: (label: string) => string,
): AuditPhrase => ({ predicate, withObject });

/**
 * A phrase whose object the same actor can add more than once, so the rail can
 * tell the repeats apart (PB-5174).
 *
 * `verb` and `noun` are separate so the counted wording is built rather than
 * retyped: "added" + "sticky note" yields "added sticky note 3" beside the
 * unnumbered "added a sticky note", and the article only has to be dropped in
 * one place.
 *
 * It does NOT make the two forms impossible to contradict, and an earlier
 * revision of this comment claimed it did. `predicate` arrives independently of
 * `verb`, so `countedPhrase("added a sticky note", "removed", "sticky note")`
 * compiles happily and renders "added a sticky note" beside "removed sticky
 * note 3" for one action. `countedObjectPhrase` below is looser still — it
 * states the verb THREE times, in the predicate, in the `withObject` lambda and
 * as `verb`. Deriving all of them from one string is not available: articles
 * vary with the noun, and `shape_placed` reads "placed Workcell" rather than
 * verb + article + noun.
 *
 * So the invariant is enforced by a test instead of by the shape — see "every
 * counted phrase agrees with its own predicate" in `entity-audit.spec.ts`,
 * which walks every entry carrying `withCount` and fails if the counted wording
 * opens on a different verb.
 */
const countedPhrase = (
  predicate: string,
  verb: string,
  noun: string,
): AuditPhrase => ({
  predicate,
  withCount: (n) => `${verb} ${noun} ${n}`,
});

/**
 * Both: names the object from a catalogue AND counts the actor's repeats.
 *
 * The verb is stated here as well as inside `withObject` and `predicate`; see
 * the note on {@link countedPhrase} for why that cannot be factored out and
 * which test holds the three in agreement.
 */
const countedObjectPhrase = (
  predicate: string,
  withObject: (label: string) => string,
  verb: string,
  fallbackNoun: string,
): AuditPhrase => ({
  predicate,
  withObject,
  /* The label is preferred, but a row whose kind the catalogue cannot label
     still needs a countable noun — otherwise a new catalogue kind would silently
     lose its number as well as its name. */
  withCount: (n, label) => `${verb} ${label ?? fallbackNoun} ${n}`,
});

/**
 * "a" or "an" for one label.
 *
 * The comment above explains why the WIDGET forms carry no article: that
 * catalogue holds plurals ("Tags") and proper names, which no first-letter rule
 * can handle. This helper serves the vocabularies where the rule IS total —
 * closed, code-authored sets of SINGULAR common nouns that read wrong without
 * an article and cannot hit the plural case the widget rule fails on:
 *
 *   - `XMATRIX_ITEM_LABELS` — "annual objective", "improvement priority", …
 *   - `VSM_FLOW_LABELS` — "material flow", "information flow"
 *
 * The flow labels were added after review caught "added a information flow":
 * they had a hardcoded "a", which is exactly the trap this helper exists to
 * close, and the vowel-initial member made it visible.
 *
 * Do NOT reach for this from a widget or shape phrase; check the vocabulary is
 * closed and singular first.
 */
const withArticle = (label: string): string =>
  `${/^[aeiou]/i.test(label) ? "an" : "a"} ${label}`;

/**
 * Catalogue label for an allowlisted `objectKind`, or `undefined`.
 *
 * OPTION A (PB-5161): `WIDGET_LABELS` lives in `@playbookai/widget-schemas`, a
 * leaf data package (types + zod, no React) this package may depend on.
 * Resolving here rather than in `AuditFeed` keeps `packages/ui` data-blind
 * (ADR-0009), which is the same reason this phrase table lives in
 * `api-clients` at all.
 *
 * SIX vocabularies, tried in order, each closed and code-authored: widget
 * kinds, VSM artifact-card ids, VSM arrow flow kinds, Hoshin X-Matrix
 * quadrants, Hoshin workshop steps, and VSM map symbol kinds. They are mutually
 * disjoint — each branch below says why it cannot collide with the ones above
 * it — so one wire field can serve them all and the first hit is the answer.
 *
 * This USED to read "only widget kinds", on the reasoning that VSM map shapes
 * live in the Yjs `nodes` map which the projection diff did not walk, so a
 * shape kind could never arrive and a `VSM_SHAPE_CATALOGUE` branch would be
 * dead code. PB-5168 made the diff walk `nodes` and `edges` and emit
 * `shape_*` / `arrow_*` / `lane_*` rows, so that premise is gone and the branch
 * is live — it is the last one below. The paragraph is rewritten rather than
 * deleted because "shapes cannot reach here" is exactly the kind of claim a
 * later reader would otherwise trust over the code.
 *
 * `Object.hasOwn` before the read, not `map[kind] ?? …` — see {@link lookupLabel},
 * which every keyed vocabulary goes through: `kind` is an untrusted wire string,
 * and a prototype key ("constructor", "toString") indexes a plain object literal
 * to a FUNCTION, which would then be interpolated into the phrase. The symbol
 * catalogue is a tuple rather than a record, so it is immune by shape.
 *
 * PACKAGE-INTERNAL despite the `export`, which is worth stating because the
 * keyword reads like public API and is not. `index.ts` deliberately does not
 * re-export it, so it is not part of this package's surface and nothing
 * outside `api-clients` can reach it; the `export` exists only because
 * `entity-audit.ts` is a different module and has no other way to import it.
 * It stays HERE rather than moving into that module because it reads the
 * catalogue this file already owns — the phrase table and the label lookup
 * are one concern, and splitting them would put a catalogue read in the
 * mapper.
 */
export function auditObjectLabel(objectKind: string): string | undefined {
  return (
    lookupLabel(WIDGET_LABELS, objectKind) ??
    /* VSM workshop artifact cards (PB-5166). A second closed, code-authored
       map, checked after widgets because widget rows are far more common. The
       two cannot collide: artifact ids carry an `artifact-` prefix. */
    lookupLabel(VSM_ARTIFACT_LABELS, objectKind) ??
    /* VSM arrow flow kinds (PB-5168). */
    lookupLabel(VSM_FLOW_LABELS, objectKind) ??
    /* Hoshin X-Matrix quadrants. Cannot collide with the maps above: the four
       keys are `XMatrixItemType` values, and the server only ever admits them
       on `hoshin_xmatrix_item` pairs. */
    lookupLabel(XMATRIX_ITEM_LABELS, objectKind) ??
    /* Hoshin workshop steps. Disjoint from the quadrants: these keys are
       `HoshinStep` values, admitted only on `hoshin_workshop|step_*` pairs. */
    lookupLabel(HOSHIN_STEP_LABELS, objectKind) ??
    /* VSM map symbol kinds (PB-5168). A tuple rather than a record, so it gets
       a find rather than a keyed lookup — 18 entries, and this runs once per
       rendered row. Its own `label` field is authored in code; a shape's
       user-entered label lives in Postgres and never reaches an audit row. */
    VSM_SHAPE_CATALOGUE.find((entry) => entry.kind === objectKind)?.label
  );
}

/**
 * `Object.hasOwn` before the read, not `map[key] ?? …`: `objectKind` is an
 * untrusted wire string, and a prototype key ("constructor", "toString")
 * indexes a plain object literal to a FUNCTION, which would then be
 * interpolated into the phrase.
 */
function lookupLabel(
  map: Record<string, string>,
  objectKind: string,
): string | undefined {
  if (!Object.hasOwn(map, objectKind)) return undefined;
  const label = (map as Record<string, unknown>)[objectKind];
  return typeof label === "string" ? label : undefined;
}

/**
 * `entityType|action` → phrasing.
 *
 * Only pairs reachable from a History rail's streams are listed. Anything absent
 * falls back to the humanised raw action, which is why an unmapped new action
 * degrades to readable rather than blank.
 */
export const AUDIT_PHRASES: ReadonlyMap<string, AuditPhrase> = new Map([
  // ---- SOLVE workflow ----
  ["solve|created", phrase("created this SOLVE")],
  ["solve|updated", phrase("updated the settings")],
  ["solve|deleted", phrase("deleted this SOLVE")],
  ["solve|closed", phrase("closed this SOLVE")],
  ["solve|phase_advanced", phrase("advanced the phase")],
  ["solve|phase_returned", phrase("returned to the previous phase")],
  ["solve|phase_locked", phrase("locked a phase")],
  ["solve|phase_unlocked", phrase("unlocked a phase")],
  ["solve|canvas_locked", phrase("locked the canvas")],
  ["solve|canvas_unlocked", phrase("unlocked the canvas")],
  ["solve|unlock_requested", phrase("requested the canvas be unlocked")],
  ["solve|facilitator_granted", phrase("made someone facilitator")],
  ["solve|facilitator_revoked", phrase("removed the facilitator")],

  // ---- SOLVE canvas (per-widget; only SOLVE has this granularity) ----
  [
    "solve.canvas|widget_added",
    countedObjectPhrase(
      "added a widget",
      (label) => `added ${label}`,
      "added",
      "widget",
    ),
  ],
  [
    "solve.canvas|widget_updated",
    countedObjectPhrase(
      "updated a widget",
      (label) => `updated ${label}`,
      "updated",
      "widget",
    ),
  ],
  [
    "solve.canvas|widget_removed",
    countedObjectPhrase(
      "removed a widget",
      (label) => `removed ${label}`,
      "removed",
      "widget",
    ),
  ],
  [
    "solve.canvas|widget_moved",
    countedObjectPhrase(
      "moved a widget",
      (label) => `moved ${label}`,
      "moved",
      "widget",
    ),
  ],

  /* Sticky notes (PB-5165). Their own verbs, not the widget ones: a note is a
     free-form annotation with no catalogue kind, so there is nothing to name
     after it and "edited a sticky note" is the whole truth. */
  [
    "solve.canvas|note_added",
    countedPhrase("added a sticky note", "added", "sticky note"),
  ],
  [
    "solve.canvas|note_updated",
    countedPhrase("edited a sticky note", "edited", "sticky note"),
  ],
  [
    "solve.canvas|note_removed",
    countedPhrase("removed a sticky note", "removed", "sticky note"),
  ],
  [
    "solve.canvas|note_moved",
    countedPhrase("moved a sticky note", "moved", "sticky note"),
  ],

  /* Resizable phase areas. Their own verbs for the same reason notes have
     theirs: an area is the board's structure, not a card on it, so "added a
     widget" would be a permanent mislabel in an append-only log (ADR-0005).

     Deliberately GENERIC — "a phase area", never the phase's name. The row's
     payload carries `phase`, but naming it here would need `objectKind`, and
     that one field serves several vocabularies whose key spaces must not
     collide; the SOLVE phase names are not registered there, so the label would
     resolve against whichever vocabulary answered first. The same reasoning the
     VSM phase-advance entry below records. */
  [
    "solve.canvas|phase_area_placed",
    countedPhrase("placed a phase area", "placed", "phase area"),
  ],
  [
    "solve.canvas|phase_area_resized",
    countedPhrase("resized a phase area", "resized", "phase area"),
  ],
  [
    "solve.canvas|phase_area_cleared",
    countedPhrase("cleared a phase area", "cleared", "phase area"),
  ],

  // ---- VSM workflow ----
  ["vsm|created", phrase("created this value stream")],
  ["vsm|updated", phrase("updated the settings")],
  ["vsm|deleted", phrase("deleted this value stream")],
  ["vsm|canvas_locked", phrase("locked the canvas")],
  ["vsm|canvas_unlocked", phrase("unlocked the canvas")],
  ["vsm|owner_transferred", phrase("transferred ownership")],
  /* Present in real data and previously UNMAPPED, so the rail rendered the
     raw action upper-cased — "PHASE ADVANCED" — which is the exact failure
     this table was introduced to remove. VSM's phases are the workshop's, not
     SOLVE's, so the wording says so. */
  /* Stays GENERIC, and its Hoshin twin `hoshin_workshop|step_advanced` does
     not — an asymmetry worth explaining, because naming the phase here was
     attempted and is NOT SAFE.
     One `objectKind` field can serve many vocabularies only while their key
     spaces cannot collide, and VSM phase ids break that: `sipoc` is BOTH a
     `VsmPhase` and a widget kind. `auditObjectLabel` returns the first hit and
     consults widget kinds first, so a `phase_advanced` row carrying `sipoc`
     would resolve the WIDGET label — right today by coincidence, silently wrong
     the moment either label changes. The disjointness guard in
     `entity-audit.spec.ts` catches exactly this and did.
     Naming it needs a namespaced value (`phase:sipoc`) across the wire
     contract, the OpenAPI description and both label maps — a convention
     change, not a phrase change, so it is deliberately out of scope here. */
  ["vsm|phase_advanced", phrase("advanced the workshop phase")],
  ["vsm|facilitator_granted", phrase("made someone facilitator")],
  ["vsm|facilitator_revoked", phrase("removed the facilitator")],
  /* PB-5441 dev QA: written and never phrased, so the rail showed the raw key
     in capitals — "MAP PROPOSED CHANGE APPLIED". A Player AI apply keeps its
     actor, the person who approved the change, so the phrase says what that
     person did. */
  ["vsm|map_proposed_change_applied", phrase("applied a Player AI map change")],
  [
    "vsm|kaizen_proposed_change_applied",
    phrase("applied a Player AI Kaizen change"),
  ],
  ["vsm|workshop_started", phrase("started the live workshop")],
  [
    "vsm|workshop_returned_to_preparation",
    phrase("returned the workshop to individual mode"),
  ],
  ["vsm|time_display_unit_changed", phrase("changed the map time unit")],
  ["vsm|lane_limit_changed", phrase("changed the swimlane limit")],

  /* VSM names what changed, like SOLVE: the projection delivers ids and kinds,
     so a live flush writes one row per widget. The old note here said
     "edited the canvas" was "the truthful ceiling until the write side emits
     per-widget rows" — the write side now does, for every VSM object family,
     so the ceiling is gone and `canvas_projected` is a filtered receipt rather
     than a fallback phrasing. */
  [
    "vsm.canvas|widget_added",
    countedObjectPhrase(
      "added a widget",
      (label) => `added ${label}`,
      "added",
      "widget",
    ),
  ],
  [
    "vsm.canvas|widget_updated",
    countedObjectPhrase(
      "updated a widget",
      (label) => `updated ${label}`,
      "updated",
      "widget",
    ),
  ],
  [
    "vsm.canvas|widget_removed",
    countedObjectPhrase(
      "removed a widget",
      (label) => `removed ${label}`,
      "removed",
      "widget",
    ),
  ],
  [
    "vsm.canvas|widget_moved",
    countedObjectPhrase(
      "moved a widget",
      (label) => `moved ${label}`,
      "moved",
      "widget",
    ),
  ],

  /* Sticky notes (PB-5165). Their own verbs, not the widget ones: a note is a
     free-form annotation with no catalogue kind, so there is nothing to name
     after it and "edited a sticky note" is the whole truth. */
  [
    "vsm.canvas|note_added",
    countedPhrase("added a sticky note", "added", "sticky note"),
  ],
  [
    "vsm.canvas|note_updated",
    countedPhrase("edited a sticky note", "edited", "sticky note"),
  ],
  [
    "vsm.canvas|note_removed",
    countedPhrase("removed a sticky note", "removed", "sticky note"),
  ],
  [
    "vsm.canvas|note_moved",
    countedPhrase("moved a sticky note", "moved", "sticky note"),
  ],
  /* PB-5168 — the map's own objects, the original report that started this
     effort. Shapes and arrows name themselves; a swimlane cannot, because its
     only distinguishing field is a user-authored label. */
  [
    "vsm.canvas|shape_placed",
    countedObjectPhrase(
      "placed a shape",
      (label) => `placed ${label}`,
      "placed",
      "shape",
    ),
  ],
  [
    "vsm.canvas|shape_removed",
    countedObjectPhrase(
      "removed a shape",
      (label) => `removed ${label}`,
      "removed",
      "shape",
    ),
  ],
  [
    "vsm.canvas|shape_moved",
    countedObjectPhrase(
      "moved a shape",
      (label) => `moved ${label}`,
      "moved",
      "shape",
    ),
  ],
  /* The ONE countable family left deliberately uncounted (PB-5174): this
     phrase puts the object mid-sentence, so there is no position a trailing
     ordinal could occupy — "changed what Workcell displays 3" is not English.
     `withCount` is optional precisely so a phrase whose shape rejects a number
     can decline one, rather than forcing every family into one template. */
  [
    "vsm.canvas|shape_display_updated",
    objectPhrase(
      "changed what a shape displays",
      (label) => `changed what ${label} displays`,
    ),
  ],
  [
    "vsm.canvas|arrow_added",
    countedObjectPhrase(
      "added an arrow",
      (label) => `added ${withArticle(label)}`,
      "added",
      "arrow",
    ),
  ],
  [
    "vsm.canvas|arrow_removed",
    countedObjectPhrase(
      "removed an arrow",
      (label) => `removed ${withArticle(label)}`,
      "removed",
      "arrow",
    ),
  ],
  [
    "vsm.canvas|arrow_updated",
    countedObjectPhrase(
      "updated an arrow",
      (label) => `updated ${withArticle(label)}`,
      "updated",
      "arrow",
    ),
  ],
  /* The per-flush fan-out ceiling (`MAX_AUDIT_ROWS_PER_FLUSH`). All three
     canvas modules write this one, so all three need the key — an unphrased
     pair renders as the raw upper-cased enum "CANVAS BULK EDITED".

     Deliberately NOT "edited the canvas": that phrase was retired because it
     fired when nothing nameable had changed. This is the opposite case — more
     changed than a rail can list — and the row's `objectCount` says how much.
     The copy stays count-free because the phrase map keys on `objectKind`
     only and never reads the payload. */
  [
    "solve.canvas|canvas_bulk_edited",
    phrase("made a bulk change to the canvas"),
  ],
  ["vsm.canvas|canvas_bulk_edited", phrase("made a bulk change to the canvas")],
  [
    "hoshin.canvas|canvas_bulk_edited",
    phrase("made a bulk change to the canvas"),
  ],

  [
    "vsm.canvas|lane_added",
    countedPhrase("added a swimlane", "added", "swimlane"),
  ],
  [
    "vsm.canvas|lane_removed",
    countedPhrase("removed a swimlane", "removed", "swimlane"),
  ],
  /* Neutral on purpose (PB-5441 UX-6): a lane is `moved` whenever its place
     changes — a restack that slides it down as a lane above grows, not only a
     reorder — and `updated` when anything else does, its height as often as
     its name. "reordered" and "renamed" named a change nobody made. */
  [
    "vsm.canvas|lane_moved",
    countedPhrase("moved a swimlane", "moved", "swimlane"),
  ],
  [
    "vsm.canvas|lane_updated",
    countedPhrase("changed a swimlane", "changed", "swimlane"),
  ],

  /* PB-5166: filling in a workshop artifact card names the card. The id is
     resolved to its title client-side, exactly as a widget kind is. */
  [
    "vsm.canvas|artifact_updated",
    countedObjectPhrase(
      "updated a workshop card",
      (label) => `updated ${label}`,
      "updated",
      "workshop card",
    ),
  ],
  /* Kept for the rows already in the log. `canvas_projected` is now an
     internal receipt (`INTERNAL_RECEIPT_ACTIONS`), so no new one reaches a
     rail — but `audit_log` is append-only (ADR-0005) and the historic rows
     still resolve through this map on any surface that does not filter. */
  ["vsm.canvas|canvas_projected", phrase("edited the canvas")],

  /* ---- VSM relational children ----
     Reachable as of PB-4759: `VsmAuditReadAuthorizer.resolveAuditRefs` expands
     an admitted value stream into its artifact widgets, map shapes and Kaizen
     scores. These are the substance of a workshop — every charter and SIPOC
     save, every shape, every Kaizen decision — and until the refs existed they
     were written and never read.

     `vsm_artifact` rows are keyed by the WIDGET id and `vsm_shape` /
     `vsm_kaizen_score` by the SHAPE id, which is why they are separate entity
     types rather than payload fields on `vsm`. */
  ["vsm_artifact|artifact_created", phrase("started a workshop card")],
  ["vsm_artifact|artifact_updated", phrase("filled in a workshop card")],
  /* PB-5441 d7: a person's edit to a workshop card that merges by operations
     (SIPOC and others) is written as a command, so it reads the same as the
     whole-card save above. */
  [
    "vsm_artifact|artifact_command_applied",
    phrase("filled in a workshop card"),
  ],
  [
    "vsm_artifact|sipoc_seeded",
    phrase("seeded the current-state map from SIPOC"),
  ],
  [
    "vsm_artifact|promotion_row_created",
    phrase("added a Kaizen to the implementation plan"),
  ],
  // PB-5441 dev QA: the vsm_artifact and vsm_kaizen_score rows below were
  // written and never phrased, so they rendered as raw keys in capitals.
  [
    "vsm_artifact|artifact_proposed_change_applied",
    phrase("applied a Player AI change to a workshop card"),
  ],
  [
    "vsm_artifact|sipoc_created_and_seeded",
    phrase("started the SIPOC and the current-state map"),
  ],
  [
    "vsm_artifact|promotion_row_deleted",
    phrase("removed a Kaizen from the implementation plan"),
  ],
  ["vsm_shape|shape_created", phrase("added a shape")],
  ["vsm_kpi_link|kpi_link_created", phrase("linked a KPI")],
  ["vsm_kpi_link|kpi_link_removed", phrase("unlinked a KPI")],
  [
    "vsm_preparation_document_binding|preparation_document_binding.created",
    phrase("added a preparation file"),
  ],
  [
    "vsm_preparation_document_binding|preparation_document_binding.updated",
    phrase("updated a preparation file"),
  ],
  [
    "vsm_shape|shape_created_from_sipoc",
    phrase("added a shape from the SIPOC"),
  ],
  ["vsm_shape|shape_updated", phrase("edited a shape")],
  ["vsm_shape|shape_deleted", phrase("removed a shape")],
  ["vsm_shape|shape_restored", phrase("restored a shape")],
  ["vsm_shape|kaizen_promoted", phrase("promoted a Kaizen")],
  ["vsm_shape|kaizen_unpromoted", phrase("returned a Kaizen to the map")],
  ["vsm_kaizen_score|kaizen_score_set", phrase("scored a Kaizen")],
  ["vsm_kaizen_score|kaizen_parked", phrase("parked a Kaizen")],
  ["vsm_kaizen_score|kaizen_unparked", phrase("unparked a Kaizen")],
  ["vsm_kaizen_score|kaizen_score_cleared", phrase("cleared a Kaizen score")],
  [
    "vsm_kaizen_score|kaizen_matrix_placed",
    phrase("placed a Kaizen on the matrix"),
  ],
  ["vsm_kaizen_score|kaizen_score_deleted", phrase("removed a Kaizen score")],
  ["vsm_kaizen_target|kaizen_target_created", phrase("anchored a Kaizen")],
  ["vsm_kaizen_target|kaizen_target_deleted", phrase("unanchored a Kaizen")],

  /* Not "edited the canvas" any more, and no longer a vague row at all.
     `hoshin-canvas.doc-type` writes this ONLY when the flush carried nothing
     discrete AND the collab diff reported the X-Matrix as changed — a
     signals-only or idle flush now writes the filtered `projection_ping`
     instead. So by construction the one thing this row can mean is that the
     matrix moved, and the matrix is precisely what the diff cannot itemise
     (a per-plan singleton with no per-item identity in Yjs, PB-272).
     Naming it is therefore both more useful and more accurate than the old
     generic predicate, which sat beside the specific lines for the same
     workshop and read as noise.

     KNOWN COST, stated rather than hidden: rows written BEFORE the producer
     sent `xmatrixChanged` are relabelled by this too, and a minority of them
     were signals-only pings, not matrix edits. Nothing distinguishes the two
     in the stored payload — both are 0/0/0/0 — so no read-time rule could
     separate them; the old copy was equally unable to, it merely hid the
     ambiguity behind vaguer words. The affected population is dev and staging
     only (the coarse row predates no customer deployment), and the phrase map
     is a presentation layer re-derived on every read, so this changes no
     stored row: `audit_log` stays append-only (ADR-0005). */
  ["hoshin.canvas|canvas_edited", phrase("updated the X-Matrix")],

  /* Hoshin's coarse `canvas_edited` above is the debounced Yjs flush. These two
     are different rows and CAN be specific: Player AI's workshop canvas writes
     go through the product API one widget at a time, so the row names a single
     widget rather than collapsing a flush. Same phrasing as solve.canvas, which
     has the same per-widget shape. */
  /* One call site, two entityTypes: `canvasAuditEntityType()` chooses at
     runtime, so a key here without one below (or the reverse) leaves the other
     rendering as a raw upper-cased enum. The VSM side names the widget; the
     Hoshin side keeps the plain predicate, which is a deliberate asymmetry
     rather than an oversight — see the DTO's note on the pair map. */
  [
    "vsm.canvas|widget_created",
    countedObjectPhrase(
      "added a widget",
      (label) => `added ${label}`,
      "added",
      "widget",
    ),
  ],
  [
    "hoshin.canvas|widget_created",
    countedPhrase("added a widget", "added", "widget"),
  ],

  /* PB-5165: Hoshin's live flush now fans out per object like SOLVE and VSM,
     so these name what changed. `canvas_edited` above SURVIVES and is not dead
     vocabulary — an X-Matrix-only edit still reaches the projection as 0/0/0/0
     (the matrix is a per-plan singleton the diff does not walk, PB-272), and
     that flush keeps the coarse row because "edited the canvas" is the only
     honest thing to say about it.

     `widget_updated` is shared with the Player AI product-API write that
     predates this, which is fine: both mean the same thing to a reader, and
     the object form simply adds the name when the row carries a kind. */
  [
    "hoshin.canvas|widget_added",
    countedObjectPhrase(
      "added a widget",
      (label) => `added ${label}`,
      "added",
      "widget",
    ),
  ],
  [
    "hoshin.canvas|widget_updated",
    countedObjectPhrase(
      "updated a widget",
      (label) => `updated ${label}`,
      "updated",
      "widget",
    ),
  ],
  [
    "hoshin.canvas|widget_removed",
    countedObjectPhrase(
      "removed a widget",
      (label) => `removed ${label}`,
      "removed",
      "widget",
    ),
  ],
  [
    "hoshin.canvas|widget_moved",
    countedObjectPhrase(
      "moved a widget",
      (label) => `moved ${label}`,
      "moved",
      "widget",
    ),
  ],

  /* Sticky notes (PB-5165). Their own verbs, not the widget ones: a note is a
     free-form annotation with no catalogue kind, so there is nothing to name
     after it and "edited a sticky note" is the whole truth. */
  [
    "hoshin.canvas|note_added",
    countedPhrase("added a sticky note", "added", "sticky note"),
  ],
  [
    "hoshin.canvas|note_updated",
    countedPhrase("edited a sticky note", "edited", "sticky note"),
  ],
  [
    "hoshin.canvas|note_removed",
    countedPhrase("removed a sticky note", "removed", "sticky note"),
  ],
  [
    "hoshin.canvas|note_moved",
    countedPhrase("moved a sticky note", "moved", "sticky note"),
  ],

  // ---- Hoshin workflow ----
  ["hoshin_plan|created", phrase("created this plan")],
  ["hoshin_plan|updated", phrase("updated the plan settings")],
  ["hoshin_plan|deleted", phrase("deleted this plan")],
  ["hoshin_plan|activate", phrase("activated the plan")],
  ["hoshin_plan|close", phrase("closed the plan")],
  ["hoshin_plan|archive", phrase("archived the plan")],
  ["hoshin_plan|submitForReview", phrase("submitted the plan for review")],
  ["hoshin_plan|returnToActive", phrase("returned the plan to active")],
  ["hoshin_plan|canvas_locked", phrase("locked the canvas")],
  ["hoshin_plan|canvas_unlocked", phrase("unlocked the canvas")],
  ["hoshin_plan|owner_transferred", phrase("transferred ownership")],

  /* ---- Hoshin workshop and X-Matrix ----
     Reachable as of PB-4759: `HoshinAuditReadAuthorizer.resolveAuditRefs`
     expands an admitted plan into these child entities, so the plan rail now
     receives them. Before that they were written and never read, which is why
     a completed six-step workshop rendered as "created this plan" and
     "activated the plan" and nothing else.

     WORDED FOR A READER, NOT FOR THE SCHEMA. The room says "theme", not
     "group"; "moved the workshop on" rather than "advanced the step", because
     the step names are already shown beside the rail. */
  ["hoshin_workshop|created", phrase("set up the workshop")],
  ["hoshin_workshop|started", phrase("started the workshop")],
  /* Names the step the room is NOW on. "on a step" made the reader count; the
     destination is the whole point of the row. */
  [
    "hoshin_workshop|step_advanced",
    objectPhrase(
      "moved the workshop on a step",
      (label) => `moved the workshop to ${label}`,
    ),
  ],
  [
    "hoshin_workshop|step_reverted",
    objectPhrase(
      "took the workshop back a step",
      (label) => `took the workshop back to ${label}`,
    ),
  ],
  ["hoshin_workshop|completed", phrase("closed the workshop")],
  [
    "hoshin_workshop|workshop_parent_matrix_visibility_set",
    phrase("changed whether the parent X-Matrix is shown"),
  ],
  /* Player AI acting inside the room. Phrased as what the ROOM got, with the
     agent named by the feed's own AI provenance rather than in the words. */
  ["hoshin_workshop|ai_grouping_applied", phrase("applied AI grouping")],
  [
    "hoshin_workshop|ai_impact_effort_applied",
    phrase("applied AI impact/effort scoring"),
  ],
  [
    "hoshin_workshop|ai_duplicate_replacements_added",
    phrase("merged duplicate ideas"),
  ],
  ["hoshin_workshop_idea|ai_suggestion_created", phrase("suggested an idea")],
  [
    "hoshin_workshop|prioritization_matrix_created",
    phrase("opened the prioritisation matrix"),
  ],
  [
    "hoshin_workshop|prioritization_matrix_placement_updated",
    phrase("moved an idea on the prioritisation matrix"),
  ],
  [
    "hoshin_workshop|prioritization_matrix_idea_removed",
    phrase("took an idea off the prioritisation matrix"),
  ],
  [
    "hoshin_workshop_participant|participant_added",
    phrase("added someone to the room"),
  ],
  ["hoshin_workshop_idea|idea_created", phrase("added an idea")],
  ["hoshin_workshop_idea|idea_updated", phrase("edited an idea")],
  ["hoshin_workshop_idea|idea_deleted", phrase("removed an idea")],
  ["hoshin_workshop_idea|idea_parking_changed", phrase("parked an idea")],
  ["hoshin_workshop_group|group_created", phrase("added a theme")],
  ["hoshin_workshop_group|group_updated", phrase("updated a theme")],
  ["hoshin_workshop_group|group_deleted", phrase("removed a theme")],
  ["hoshin_workshop_group|group_prioritized", phrase("prioritised a theme")],
  [
    "hoshin_workshop_group_note|idea_grouped",
    phrase("filed an idea under a theme"),
  ],
  [
    "hoshin_workshop_group_note|idea_ungrouped",
    phrase("took an idea out of its theme"),
  ],
  /* Categorize drags a note within its theme. Its own verb, not `idea_grouped`:
     the idea does not change theme, so "filed under" would be a false claim. */
  [
    "hoshin_workshop_group_note|idea_placed",
    phrase("moved an idea on the board"),
  ],
  ["hoshin_workshop_group|ai_grouping_applied", phrase("applied AI grouping")],
  ["hoshin_workshop_vote|voting_started", phrase("opened voting")],
  ["hoshin_workshop_vote|ballot_submitted", phrase("voted")],
  ["hoshin_workshop_vote|voting_closed", phrase("closed voting")],
  ["hoshin_workshop_vote|vote_cast", phrase("voted")],
  ["hoshin_workshop_vote|vote_withdrawn", phrase("withdrew a vote")],
  ["hoshin_workshop_vote|vote_submitted", phrase("voted")],
  ["hoshin_workshop_vote|vote_cleared", phrase("cleared a vote")],
  ["hoshin_workshop_vote|voting_configured", phrase("set up the vote")],
  ["hoshin_workshop_vote|voting_reset", phrase("reset the vote")],

  /* PB-5156 cascade — the child plan's first step, where the room votes which
     of the parent's improvement priorities come down. Its own entity type, so
     the wording says "the cascade" rather than borrowing the workshop's. */
  [
    "hoshin_cascade_vote|cascade_vote_started",
    phrase("opened the cascade vote"),
  ],
  [
    "hoshin_cascade_vote|cascade_vote_ballot_submitted",
    phrase("voted in the cascade"),
  ],
  [
    "hoshin_cascade_vote|cascade_vote_closed",
    phrase("closed the cascade vote"),
  ],
  ["hoshin_cascade_vote|cascade_vote_reset", phrase("reset the cascade vote")],
  [
    "hoshin_cascade_vote|cascade_priority_advanced",
    phrase("carried a parent priority down"),
  ],

  /* Ownership on the matrix. `hoshin_priority_responsibility` is its own entity
     type keyed by the responsibility row, not by the priority. */
  [
    "hoshin_priority_responsibility|priority_responsibility_set",
    phrase("gave someone a priority to own"),
  ],
  [
    "hoshin_priority_responsibility|priority_responsibility_cleared",
    phrase("removed a priority owner"),
  ],
  ["hoshin_plan|facilitator_granted", phrase("made someone facilitator")],
  ["hoshin_plan|facilitator_revoked", phrase("removed the facilitator")],
  [
    "hoshin_plan|xmatrix_derived_from_parent",
    phrase("seeded this X-Matrix from the parent plan"),
  ],
  /* The X-Matrix quadrants. ONE action covers all four item types — the
     quadrant is in the payload, not the action — so these carry an object form
     rather than four keys. `x-matrix-items.service` puts the `XMatrixItemType`
     on the row as a top-level `kind`, exactly as PB-5161's widget rows do, and
     the server allowlists it against that enum.

     WITH an article, unlike the widget forms: these labels are singular common
     nouns ("annual objective"), not proper names, so they read wrong bare. See
     `withArticle` for why a first-letter rule is safe for THIS vocabulary and
     not for the widget catalogue. */
  [
    "hoshin_xmatrix_item|xmatrix_item_created",
    objectPhrase(
      "added an X-Matrix item",
      (label) => `added ${withArticle(label)}`,
    ),
  ],
  [
    "hoshin_xmatrix_item|xmatrix_item_updated",
    objectPhrase(
      "edited an X-Matrix item",
      (label) => `edited ${withArticle(label)}`,
    ),
  ],
  [
    "hoshin_xmatrix_item|xmatrix_item_archived",
    objectPhrase(
      "archived an X-Matrix item",
      (label) => `archived ${withArticle(label)}`,
    ),
  ],
  [
    "hoshin_xmatrix_item|xmatrix_items_reordered",
    phrase("reordered the X-Matrix"),
  ],
  [
    "hoshin_xmatrix_item|xmatrix_kpi_linked",
    phrase("linked a KPI to a target"),
  ],
  [
    "hoshin_xmatrix_item|xmatrix_kpi_unlinked",
    phrase("unlinked a KPI from a target"),
  ],
  [
    "hoshin_xmatrix_relationship|xmatrix_relationship_created",
    phrase("correlated two X-Matrix items"),
  ],
  [
    "hoshin_xmatrix_relationship|xmatrix_relationship_deleted",
    phrase("removed a correlation"),
  ],
  [
    "hoshin_xmatrix_person|xmatrix_people_changed",
    phrase("changed who is named on the X-Matrix"),
  ],
  /* Bowling entries are NOT expanded into the plan rail (`resolveAuditRefs`
     excludes them as measurement, the line KPI draws at `kpi_observation`), but
     a tenant-admin read reaches them through the service's admin branch, so
     they are phrased rather than left to render as "BOWLING PLAN SET". */
  [
    "hoshin_bowling_entry|bowling_plan_set",
    phrase("set the plan line for a month"),
  ],
  [
    "hoshin_bowling_entry|bowling_actual_recorded",
    phrase("recorded a month's actual"),
  ],

  /* ---- LAMA authoring ----
     Only these three actions are reachable. `LamaAuditReadAuthorizer` admits
     exactly `lama_questionnaire` and `lama_campaign` and implements no
     `resolveAuditRefs`, so the version, category, question and membership rows
     written under their own entity types never enter this rail — phrasing them
     here would be dead vocabulary. `created` is written twice server-side (once
     by create, once by duplicate) against the same pair, so one entry covers
     both. */
  ["lama_questionnaire|created", phrase("created this questionnaire")],
  ["lama_questionnaire|updated", phrase("updated the questionnaire settings")],
  ["lama_questionnaire|deleted", phrase("deleted this questionnaire")],

  /* ---- LAMA campaigns ----
     `scheduled`, `unscheduled` and `activated` are campaign-level rows written
     by `waves.service.ts`, not by a campaign endpoint: they are the campaign's
     side of a WAVE event (first wave created, last wave deleted, first wave
     opened), which is why they are phrased as what happened to the campaign
     rather than as something done to a wave. */
  ["lama_campaign|created", phrase("created this campaign")],
  ["lama_campaign|updated", phrase("updated the campaign settings")],
  ["lama_campaign|deleted", phrase("deleted this campaign")],
  ["lama_campaign|closed", phrase("closed this campaign")],
  ["lama_campaign|scheduled", phrase("scheduled this campaign")],
  ["lama_campaign|unscheduled", phrase("returned this campaign to draft")],
  ["lama_campaign|activated", phrase("activated this campaign")],
  ["lama_campaign|draft_replaced", phrase("updated the campaign draft")],
  [
    "lama_campaign|question_subset_replaced",
    phrase("changed which questions this campaign asks"),
  ],
  [
    "lama_campaign|exclusions_replaced",
    phrase("updated the participant exclusions"),
  ],
  ["lama_campaign|policy_created", phrase("added a distribution policy")],
  ["lama_campaign|policy_updated", phrase("updated a distribution policy")],
  ["lama_campaign|policy_removed", phrase("removed a distribution policy")],
  ["lama_campaign|exported", phrase("exported a report")],

  // ---- Memberships. Keyed to the PARENT entity, so a rail can read them. ----
  ["solve_membership|solve.access_restored", phrase("restored access")],
  ["vsm_membership|vsm.access_restored", phrase("restored access")],
  ["lama_campaign_membership|lama.access_restored", phrase("restored access")],
  ["solve_membership|created", phrase("added a member")],
  ["solve_membership|updated", phrase("changed a member's access")],
  ["solve_membership|deleted", phrase("removed a member")],
  ["vsm_membership|created", phrase("added a member")],
  ["vsm_membership|updated", phrase("changed a member's access")],
  ["vsm_membership|deleted", phrase("removed a member")],
  ["hoshin_plan_membership|member_added", phrase("added a member")],
  [
    "hoshin_plan_membership|member_permission_changed",
    phrase("changed a member's access"),
  ],
  ["hoshin_plan_membership|member_removed", phrase("removed a member")],
]);

/**
 * The phrasing for one row, or `undefined` when the pair is unmapped.
 *
 * A caller falls back to the humanised raw action rather than rendering
 * nothing: the action vocabulary is open — a module can add one at any time —
 * and a blank line in a history feed is worse than an ugly one.
 */
export function auditPhraseFor(
  entityType: string,
  action: string,
): AuditPhrase | undefined {
  return AUDIT_PHRASES.get(
    `${entityType}|${normalizeAuditAction(action).action}`,
  );
}
